import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scenarioNames = {
  workplace: 'Workplace',
  medical: 'Medical',
  travel: 'Travel',
  school: 'School',
  social: 'Social'
};

export default function ScenarioFlow() {
  const { scenarioType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  
  const conditionContext = location.state?.conditionContext;
  
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(3);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initialContext, setInitialContext] = useState(
    conditionContext ? `I have ${conditionContext.name}. ` : ''
  );
  const [started, setStarted] = useState(false);

  const startScenario = async (context = null) => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/scenario/start`,
        { scenario_type: scenarioType, initial_context: context },
        { headers }
      );
      
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.question);
      setQuestionNumber(response.data.question_number);
      setTotalQuestions(response.data.total_questions);
      setStarted(true);
    } catch (error) {
      console.error('Error starting scenario:', error);
      toast.error('Failed to start coaching session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartWithContext = () => {
    startScenario(initialContext || null);
  };

  const handleSubmitAnswer = async () => {
    if (!answer.trim()) {
      toast.error('Please share your thoughts before continuing');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/scenario/answer`, {
        session_id: sessionId,
        answer: answer.trim()
      });
      
      setAnswer('');
      
      // Check if we got a result (ScriptResult) or another question
      if (response.data.recommended_action) {
        // It's a result - navigate to results page
        navigate(`/results/${sessionId}`, { state: { result: response.data } });
      } else {
        // It's another question
        setCurrentQuestion(response.data.question);
        setQuestionNumber(response.data.question_number);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipToResult = async () => {
    if (!answer.trim()) {
      toast.error('Please provide at least a brief response');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/scenario/generate-result`, {
        session_id: sessionId,
        answer: answer.trim()
      });
      
      navigate(`/results/${sessionId}`, { state: { result: response.data } });
    } catch (error) {
      console.error('Error generating result:', error);
      toast.error('Failed to generate recommendations');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (questionNumber / totalQuestions) * 100;

  // Initial setup screen
  if (!started) {
    return (
      <div className="min-h-screen pb-safe" data-testid="scenario-flow">
        <div className="max-w-lg mx-auto px-6 pt-8">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>

          <div className="mt-8 page-enter">
            <h1 className="font-heading text-3xl font-semibold text-foreground">
              {scenarioNames[scenarioType] || 'Scenario'}
            </h1>
            <p className="mt-2 text-muted-foreground">
              Let's work through this together. I'll ask a few questions to understand your situation better.
            </p>
          </div>

          <div className="mt-8 card-soft page-enter stagger-1">
            <label className="block text-foreground font-medium mb-3">
              Briefly describe your situation (optional)
            </label>
            <Textarea
              data-testid="initial-context-input"
              value={initialContext}
              onChange={(e) => setInitialContext(e.target.value)}
              placeholder="E.g., I need to ask my manager for flexible hours because of my condition..."
              className="min-h-[120px] resize-none"
            />
            <p className="text-xs text-muted-foreground mt-2">
              This helps tailor the questions to your specific situation
            </p>
          </div>

          <div className="mt-8 page-enter stagger-2">
            <Button
              type="button"
              data-testid="start-scenario-btn"
              onClick={handleStartWithContext}
              className="btn-primary w-full relative z-10"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                'Start Coaching Session'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Question flow
  return (
    <div className="min-h-screen pb-safe" data-testid="scenario-flow">
      <div className="max-w-lg mx-auto px-6 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          <span className="text-sm text-muted-foreground">
            Question {questionNumber} of {totalQuestions}
          </span>
        </div>

        {/* Progress */}
        <div className="mt-6">
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question */}
        <div className="mt-8 page-enter">
          <div className="card-soft">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <p className="text-lg text-foreground leading-relaxed" data-testid="current-question">
                {currentQuestion}
              </p>
            )}
          </div>
        </div>

        {/* Answer Input */}
        {!loading && (
          <div className="mt-6 page-enter stagger-1">
            <Textarea
              data-testid="answer-input"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Share your thoughts..."
              className="min-h-[120px] resize-none"
              disabled={submitting}
            />
          </div>
        )}

        {/* Actions */}
        {!loading && (
          <div className="mt-6 space-y-3 page-enter stagger-2">
            <Button
              data-testid="submit-answer-btn"
              onClick={handleSubmitAnswer}
              className="btn-primary w-full"
              disabled={submitting || !answer.trim()}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  {questionNumber >= totalQuestions ? 'Get My Script' : 'Continue'}
                </>
              )}
            </Button>

            {questionNumber >= 2 && (
              <Button
                data-testid="skip-to-result-btn"
                variant="ghost"
                onClick={handleSkipToResult}
                className="w-full text-muted-foreground hover:text-foreground"
                disabled={submitting}
              >
                Skip to results
              </Button>
            )}
          </div>
        )}

        {/* Support Note */}
        <div className="mt-10 text-center page-enter stagger-3">
          <p className="text-sm text-muted-foreground">
            Take your time. There are no wrong answers here.
          </p>
        </div>
      </div>
    </div>
  );
}
